/**
 * The swipe deck (brief §9a).
 *
 * Which places are worth asking about is decided **on the device**, from the hot-cell feed
 * the geofence already holds and the dwell timer it already keeps. No extra request, no extra
 * position, and nothing that says where anybody has been.
 *
 * `shouldOfferCard` in `domain/passby.ts` is the rule; this file is the bookkeeping around
 * it — which cards are open, which one is on top, the five seconds in which the last swipe
 * can be taken back, and what the others had said once it no longer can (amendment 12).
 */

import type { CardCandidate, Crowd, Swipe } from '$domain/passby.ts';
import { shouldOfferCard, trimDeck, UNDO_WINDOW_MS, voteFor } from '$domain/passby.ts';
import { alerts, type FeedCell } from './alerts.svelte.ts';
import { ApiError, request } from './api.ts';
import { outbox } from './outbox.svelte.ts';

export type Card = FeedCell;

/** What is waiting to be undone, and until when. */
type PendingUndo = { cell: string; swipe: Swipe; until: number };

/** A Schwarmgefühl verdict to show: what you said, and how the people before you had judged it. */
export type Reveal = Crowd & { vote: 1 | -1 };

/** Long enough to read twice, short enough that it is gone before it is in the way. */
const REVEAL_SHOWN_MS = 8000;
/**
 * The swipe is queued, not sent (§14a.3), so when the undo window closes the server may not
 * have it yet. It usually has — `enqueue` flushes at once — and a few retries cover the rest.
 * Offline, there is simply no reveal this time; the verdict is still written when the outbox
 * catches up, and still counts.
 */
const REVEAL_ATTEMPTS = 3;
const REVEAL_RETRY_MS = 1500;

class Deck {
  /** Cells swiped in this session, so a card does not come back before the feed refreshes. */
  #answered = new Set<string>();
  #undo = $state<PendingUndo | undefined>();
  #undoTimer: ReturnType<typeof setTimeout> | undefined;
  #reveal = $state<Reveal | undefined>();
  #revealTimer: ReturnType<typeof setTimeout> | undefined;

  open = $state(false);
  busy = $state(false);
  error = $state('');

  /**
   * The cards on offer right now.
   *
   * Derived rather than accumulated: the inputs — the feed, the dwell timer, the clock — all
   * move on their own, and a stored list would have to be invalidated by each of them. The
   * busiest first, because if somebody answers one card it should be the one worth asking.
   */
  readonly cards = $derived.by<Card[]>(() => {
    const now = Date.now();
    const dwell = alerts.dwell;
    const offered = alerts.hotCells.filter((cell) => {
      if (this.#answered.has(cell.cell)) return false;
      const candidate: CardCandidate = {
        cell: cell.cell,
        sightingCount: cell.sightingCount,
        lastSightingAt: cell.lastSightingAt,
        allMine: cell.allMine,
      };
      return shouldOfferCard({
        candidate,
        dwell,
        judgedAt: cell.judgedAt === null ? undefined : Date.parse(cell.judgedAt),
        now,
      }).ok;
    });
    return trimDeck([...offered].sort((a, b) => b.gdi - a.gdi));
  });

  readonly top = $derived(this.cards[0]);
  readonly undoable = $derived(this.#undo);
  readonly revealed = $derived(this.#reveal);

  /**
   * Record a swipe.
   *
   * The card leaves the deck first and the request goes second. That order is deliberate: the
   * gesture is the answer, and a stack that hesitates while a request is in flight is a stack
   * that gets swiped twice.
   */
  async swipe(cell: string, swipe: Swipe): Promise<void> {
    this.#answered.add(cell);
    this.error = '';
    if (voteFor(swipe) === 0 && swipe !== 'skip') return;

    // Whatever was waiting to be undone can no longer be: this swipe takes its place. So the
    // previous card's verdict is due now, not whenever its own five seconds would have run out.
    this.#settle();
    this.#undo = { cell, swipe, until: Date.now() + UNDO_WINDOW_MS };
    this.#undoTimer = setTimeout(() => this.#settle(), UNDO_WINDOW_MS);
    try {
      const position = alerts.position;
      if (position === undefined) throw new Error('keine Position');
      // Queued rather than sent (brief §14a.3). A card stack that pauses on every swipe to
      // wait for a round trip is a card stack nobody finishes.
      await outbox.enqueue('passby', { cell, swipe, lat: position.lat, lon: position.lon });
    } catch (cause) {
      this.error = cause instanceof Error ? cause.message : 'Konnte nicht gespeichert werden.';
      // Put it back: an answer the server did not take is an answer that was not given.
      this.#answered.delete(cell);
      this.#clearUndo();
    }
  }

  async undo(): Promise<void> {
    const pending = this.#undo;
    if (pending === undefined || Date.now() > pending.until) return;
    // Cleared, not settled: a swipe taken back has nothing to reveal.
    this.#clearUndo();
    this.busy = true;
    try {
      await request(`/api/cells/${pending.cell}/passby`, { method: 'DELETE' });
      this.#answered.delete(pending.cell);
    } catch {
      // The vote stands. Saying so is better than a button that silently does nothing.
      this.error = 'Rückgängig hat nicht geklappt.';
    } finally {
      this.busy = false;
    }
  }

  dismissUndo(): void {
    this.#settle();
  }

  #clearUndo(): void {
    clearTimeout(this.#undoTimer);
    this.#undoTimer = undefined;
    this.#undo = undefined;
  }

  /**
   * The undo window for the pending swipe is over: stop offering it and reveal its verdict.
   *
   * Before this existed nothing ever cleared the undo, so "Rückgängig" stayed on screen after
   * its five seconds and, pressed, silently did nothing — the one kind of button the undo code
   * itself says is worse than none. The timer that ends the window is also what ends that.
   */
  #settle(): void {
    const pending = this.#undo;
    this.#clearUndo();
    if (pending !== undefined && voteFor(pending.swipe) !== 0) void this.#revealFor(pending.cell);
  }

  async #revealFor(cell: string): Promise<void> {
    for (let attempt = 1; attempt <= REVEAL_ATTEMPTS; attempt += 1) {
      try {
        this.#show(await request<Reveal>(`/api/cells/${cell}/passby/reveal`, { method: 'POST' }));
        return;
      } catch (cause) {
        // A 404 is "not on the server yet". Anything else — offline included — means no reveal
        // this time, and saying nothing is right: the swipe itself did go through.
        if (!(cause instanceof ApiError && cause.status === 404)) return;
        if (attempt < REVEAL_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, REVEAL_RETRY_MS));
        }
      }
    }
  }

  #show(reveal: Reveal): void {
    clearTimeout(this.#revealTimer);
    this.#reveal = reveal;
    this.#revealTimer = setTimeout(() => {
      this.#reveal = undefined;
    }, REVEAL_SHOWN_MS);
  }
}

export const deck = new Deck();
